import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Payer } from '../types/api';
import { payersService } from '../services/payersService';

interface PayersState {
  payers: Payer[];
  loading: boolean;
  error: string | null;
}

const initialState: PayersState = {
  payers: [],
  loading: false,
  error: null,
};

// Async thunk for fetching payers
export const fetchPayers = createAsyncThunk(
  'payers/fetchPayers',
  async (tenantId: string, { rejectWithValue }) => {
    try {
      // Always fetch fresh payers data - no caching
      const response = await payersService.getPayersList(tenantId);
      return response.data;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch payers';
      return rejectWithValue(errorMessage);
    }
  },
);

const payersSlice = createSlice({
  name: 'payers',
  initialState,
  reducers: {
    clearPayers: (state) => {
      state.payers = [];
      state.error = null;
    },
    clearPayersError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayers.fulfilled, (state, action) => {
        state.loading = false;
        // Only update if the data actually changed
        if (JSON.stringify(state.payers) !== JSON.stringify(action.payload)) {
          state.payers = action.payload;
        }
      })
      .addCase(fetchPayers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearPayers, clearPayersError } = payersSlice.actions;
export default payersSlice.reducer;
